import { getItemByLabelFactory, getItemByIdFactory } from './_common'
import {
  assertIsASelectItem,
  assertIsNumberAndIsGreaterThanZero,
  type ISelectItem,
} from '~/types/models'

export enum OpportunityMSServicesStatusIds {
  BID_NEEDED = 1,
  RFQ_SENT = 2,
  BID_RCVD = 3,
  BID_ACCEPTED = 4,
  BID_REJECTED = 5,
  /**
   * Set by "Assign to Contract", which writes the contract too; cleared by
   * "Remove from Contract" (-> Reopened) and advanced to CLOSED_WON when the
   * contract is signed (buildContractSignActions). Never set by hand.
   *
   * ⚠ Was READY_FOR_CONTRACT, a manual step that unlocked assignment, until
   * WXTG/cynque-api-server#1816. The id is unchanged, so a row the server
   * still labels 'Ready For Contract' is this status (see setUpMSService).
   */
  ASSIGNED_TO_CONTRACT = 6,
  CLOSED = 7,
  CLOSED_WON = 8,
  REOPENED = 9,
  NOT_SET = 11,
}

export function isAnyOpenOpportunityMSServiceStatus(status: ISelectItem) {
  assertIsASelectItem(isAnyOpenOpportunityMSServiceStatus.name, status)
  assertIsNumberAndIsGreaterThanZero(status.id)
  return [
    OpportunityMSServicesStatusIds.BID_NEEDED,
    OpportunityMSServicesStatusIds.RFQ_SENT,
    OpportunityMSServicesStatusIds.BID_RCVD,
    OpportunityMSServicesStatusIds.BID_ACCEPTED,
    OpportunityMSServicesStatusIds.BID_REJECTED,
    OpportunityMSServicesStatusIds.ASSIGNED_TO_CONTRACT,
    OpportunityMSServicesStatusIds.REOPENED,
  ].includes(status.id)
}

/**
 * The statuses from which an msService may be assigned to a contract. The
 * caller also requires `doInclude` and no `contract`.
 *
 * ASSIGNED_TO_CONTRACT is here only for a legacy row: one set "Ready for
 * Contract" before #1816 and never assigned, so it carries no contract. Bid
 * Rejected is deliberately NOT assignable; Closed and Closed Won never are.
 */
export function isAssignableOpportunityMSServiceStatus(status: ISelectItem) {
  return [
    OpportunityMSServicesStatusIds.BID_NEEDED,
    OpportunityMSServicesStatusIds.RFQ_SENT,
    OpportunityMSServicesStatusIds.BID_RCVD,
    OpportunityMSServicesStatusIds.BID_ACCEPTED,
    OpportunityMSServicesStatusIds.ASSIGNED_TO_CONTRACT,
    OpportunityMSServicesStatusIds.REOPENED,
  ].includes(status.id as number)
}

export function getOpportunityMSServicesStatii(): ISelectItem[] {
  return [
    { id: OpportunityMSServicesStatusIds.BID_NEEDED, label: 'Bid Needed' },
    { id: OpportunityMSServicesStatusIds.RFQ_SENT, label: 'RFQ Sent' },
    { id: OpportunityMSServicesStatusIds.BID_RCVD, label: 'Bid Rcvd' },
    { id: OpportunityMSServicesStatusIds.BID_ACCEPTED, label: 'Bid Accepted' },
    { id: OpportunityMSServicesStatusIds.BID_REJECTED, label: 'Bid Rejected' },
    {
      id: OpportunityMSServicesStatusIds.ASSIGNED_TO_CONTRACT,
      label: 'Assigned to Contract',
      disabled: true,
    },
    {
      id: OpportunityMSServicesStatusIds.CLOSED,
      label: 'Closed',
      disabled: true,
    },
    {
      id: OpportunityMSServicesStatusIds.CLOSED_WON,
      label: 'CLOSED WON',
      disabled: true,
    },
    {
      id: OpportunityMSServicesStatusIds.REOPENED,
      label: 'Reopened',
      disabled: true,
    },
    {
      id: OpportunityMSServicesStatusIds.NOT_SET,
      label: 'Not Set',
      disabled: true,
    },
  ]
}

export const getOpportunityMSServiceStatusByLabel = getItemByLabelFactory(
  getOpportunityMSServicesStatii
)
export const getOpportunityMSServiceStatusById = getItemByIdFactory(
  getOpportunityMSServicesStatii
)
